import { useMutation, useQueryClient } from '@tanstack/react-query';

import { TarActionItemCode } from '@ekhodealer/ekho-common';

import type { GenerateTempTagResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * Input type for generating a temp tag
 * Supports both Ekho and external workflows
 */
export interface GenerateTempTagInput {
	/** Temp tag number - required for EKHO_TEMP_TAG_PACKET workflow */
	tempTagNumber?: string;
	/** Insurance provider NAIC code - required for EKHO_TEMP_TAG_PACKET workflow */
	naicCode?: string;
	/** Confirmation flag - required for external workflows (BOOKLET, EXTERNAL_PORTAL, INTERNAL_PORTAL) */
	isConfirmed?: boolean;
}

/**
 * API function to generate/apply a temp tag for a purchased vehicle
 */
export const generateTempTag = async (
	servicesProviderId: string,
	orderId: string,
	data: GenerateTempTagInput,
): Promise<GenerateTempTagResponse> => {
	const response = await apiClient.post<GenerateTempTagResponse>(
		ordersEndpoints.generateTempTag(servicesProviderId, orderId),
		data,
	);
	return response.data;
};

/**
 * Hook for generating/applying a temp tag for a purchased vehicle
 * Supports both Ekho internal workflow (PDF generation) and external workflows (confirmation)
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGenerateTempTag = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: GenerateTempTagInput) => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to generate temp tag without an order ID or services provider ID.',
				);
			}

			return generateTempTag(servicesProviderId, orderId, input);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, TarActionItemCode.GENERATE_TEMP_TAG);
		},
	});
};
